import React, { useEffect, useMemo, useState } from "react";
import { FaUsers } from "react-icons/fa";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import { colors, spacing, typography, radius, inputBase, btnPrimary, btnSecondary, transitions } from "../../../utils/designTokens";
import OrdenDelDiaEditor from "./OrdenDelDiaEditor";

const VACIO = {
  tipo: "ordinaria",
  fecha: "",
  hora: "19:00",
  duracionMin: 120,
  modalidad: "presencial",
  lugar: "",
  linkManual: "",
  proveedorVideo: "manual",
  minutosSegunda: 60,
  ordenDelDia: [{ titulo: "", detalle: "" }],
};

/**
 * Proveedor de la videollamada con el que arranca el formulario: el de la
 * asamblea (video.proveedor) si ya tiene; una nueva, Zoom automático si está
 * disponible. Sin Zoom disponible (apagado o rollback) siempre "manual".
 */
export function proveedorInicial(inicial, zoomDisponible) {
  if (!zoomDisponible) return "manual";
  const actual = inicial && ((inicial.video && inicial.video.proveedor) || inicial.proveedorVideo);
  if (actual === "zoom") return "zoom";
  if (actual === "manual" || (inicial && inicial.linkManual)) return "manual";
  return "zoom";
}

const textoModalidad = (f) => {
  if (f.modalidad === "remota") return "por videollamada";
  if (f.modalidad === "mixta") return f.lugar ? `${f.lugar} o por videollamada` : "presencial o por videollamada";
  return f.lugar ? `presencial, ${f.lugar}` : "presencial";
};

const sumarMinutos = (hhmm, min) => {
  const [h, m] = String(hhmm || "00:00").split(":").map(Number);
  const total = (h * 60 + m + Number(min || 0)) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Alta / edición / reprogramación de una asamblea.
 * props: open, onClose, onSubmit(datos), inicial (asamblea o null), modo ("crear" | "editar" | "reprogramar"),
 *        edificioNombre, guardando, error,
 *        zoomDisponible (el servidor lo dice en detalle/listar: Zoom prendido para el edificio)
 *
 * En remota o mixta se elige la videollamada: "Zoom automático" (solo si
 * zoomDisponible: Habitia crea la sala y le manda a cada propietario su acceso
 * personal) o "Pegar link" (manual, como hasta ahora). datos.proveedorVideo
 * va "zoom" | "manual"; en presencial no se manda.
 */
export default function AsambleaFormModal({
  open,
  onClose,
  onSubmit,
  inicial,
  modo = "crear",
  edificioNombre,
  guardando,
  error,
  zoomDisponible = false,
}) {
  const [form, setForm] = useState(VACIO);

  useEffect(() => {
    if (!open) return;
    if (inicial) {
      setForm({
        ...VACIO,
        ...inicial,
        linkManual: inicial.linkManual || "",
        lugar: inicial.lugar || "",
        proveedorVideo: proveedorInicial(inicial, zoomDisponible),
        ordenDelDia: (inicial.ordenDelDia && inicial.ordenDelDia.length ? inicial.ordenDelDia : VACIO.ordenDelDia)
          .map((p) => ({ id: p.id, titulo: p.titulo || "", detalle: p.detalle || "" })),
      });
    } else {
      setForm({ ...VACIO, proveedorVideo: proveedorInicial(null, zoomDisponible) });
    }
  }, [open, inicial, zoomDisponible]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const vistaPrevia = useMemo(() => {
    const fecha = form.fecha
      ? new Date(`${form.fecha}T12:00:00`).toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" })
      : "(fecha)";
    return (
      `Hola Ana. Se convoca a asamblea ${form.tipo} de copropietarios de ${edificioNombre || "(edificio)"} ` +
      `para el ${fecha} a las ${form.hora || "(hora)"} (${textoModalidad(form)}). ` +
      `Si no hay quórum, la segunda convocatoria es a las ${sumarMinutos(form.hora, form.minutosSegunda)}. ` +
      "En el detalle está el orden del día. ¿Vas a asistir?"
    );
  }, [form, edificioNombre]);

  const conVideo = form.modalidad !== "presencial";
  const usaZoom = conVideo && zoomDisponible && form.proveedorVideo === "zoom";

  const enviar = (e) => {
    e.preventDefault();
    onSubmit({
      tipo: form.tipo,
      fecha: form.fecha,
      hora: form.hora,
      duracionMin: Number(form.duracionMin),
      modalidad: form.modalidad,
      lugar: form.lugar,
      // Con Zoom no hay link pegado: cada propietario recibe el suyo.
      linkManual: conVideo && !usaZoom ? form.linkManual : "",
      ...(conVideo ? { proveedorVideo: usaZoom ? "zoom" : "manual" } : {}),
      minutosSegunda: Number(form.minutosSegunda),
      ordenDelDia: form.ordenDelDia.filter((p) => p.titulo.trim()),
    });
  };

  const titulo = modo === "reprogramar" ? "Reprogramar asamblea" : modo === "editar" ? "Editar asamblea" : "Nueva asamblea";
  const fila = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[4] };
  const label = { fontSize: typography.sizes.sm, color: colors.gray600, fontWeight: 400 };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={titulo}
      size="wide"
      icon={<FaUsers />}
      footer={
        <>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="submit" form="asamblea-form" style={btnPrimary} className="habitia-btn-primary" disabled={guardando}>
            {guardando ? "Guardando…" : modo === "reprogramar" ? "Guardar y avisar a los propietarios" : "Guardar borrador"}
          </button>
        </>
      }
    >
      <form id="asamblea-form" onSubmit={enviar} style={{ display: "flex", flexDirection: "column", gap: spacing[5] }}>
        {modo === "reprogramar" && (
          <p style={{ margin: 0, fontSize: typography.sizes.base, color: colors.warningDark, background: colors.warningLight, padding: spacing[3], borderRadius: radius.sm }}>
            Si cambiás la fecha, la hora, la modalidad, el lugar o el orden del día, se avisa a todos los propietarios y tienen que volver a confirmar.
          </p>
        )}
        <div style={fila}>
          <FormField label="Tipo" htmlFor="asm-tipo" required>
            <select id="asm-tipo" className="habitia-input" style={inputBase} value={form.tipo} onChange={set("tipo")}>
              <option value="ordinaria">Ordinaria</option>
              <option value="extraordinaria">Extraordinaria</option>
            </select>
          </FormField>
          <FormField label="Fecha" htmlFor="asm-fecha" required>
            <input id="asm-fecha" type="date" className="habitia-input" style={inputBase} value={form.fecha} onChange={set("fecha")} required />
          </FormField>
          <FormField label="Hora (1ª convocatoria)" htmlFor="asm-hora" required>
            <input id="asm-hora" type="time" className="habitia-input" style={inputBase} value={form.hora} onChange={set("hora")} required />
          </FormField>
          <FormField label="2ª convocatoria (minutos después)" htmlFor="asm-segunda">
            <input id="asm-segunda" type="number" min={15} max={240} step={5} className="habitia-input" style={inputBase} value={form.minutosSegunda} onChange={set("minutosSegunda")} />
          </FormField>
          <FormField label="Duración estimada (minutos)" htmlFor="asm-duracion">
            <input id="asm-duracion" type="number" min={30} max={480} step={15} className="habitia-input" style={inputBase} value={form.duracionMin} onChange={set("duracionMin")} />
          </FormField>
        </div>

        <div style={fila}>
          <FormField label="Modalidad" htmlFor="asm-modalidad" required>
            <select id="asm-modalidad" className="habitia-input" style={inputBase} value={form.modalidad} onChange={set("modalidad")}>
              <option value="presencial">Presencial</option>
              <option value="remota">Remota (videollamada)</option>
              <option value="mixta">Mixta (presencial y videollamada)</option>
            </select>
          </FormField>
          {form.modalidad !== "remota" && (
            <FormField label="Lugar" htmlFor="asm-lugar" required>
              <input id="asm-lugar" type="text" maxLength={300} className="habitia-input" style={inputBase} value={form.lugar} onChange={set("lugar")} placeholder="Salón del edificio" required />
            </FormField>
          )}
          {conVideo && !usaZoom && (
            <FormField label="Link de la videollamada (opcional)" htmlFor="asm-link">
              <input id="asm-link" type="url" className="habitia-input" style={inputBase} value={form.linkManual} onChange={set("linkManual")} placeholder="https://…" />
            </FormField>
          )}
        </div>
        {conVideo && zoomDisponible && (
          <fieldset style={S.fieldset}>
            <legend style={S.legend}>Videollamada</legend>
            <div style={S.opciones}>
              {[
                {
                  valor: "zoom",
                  titulo: "Zoom automático",
                  texto: "Habitia crea la sala y le manda a cada propietario su acceso personal 15 minutos antes.",
                },
                {
                  valor: "manual",
                  titulo: "Pegar link",
                  texto: "Pegás el link de una videollamada que ya creaste (Zoom, Meet, Teams…).",
                },
              ].map((op) => {
                const elegida = form.proveedorVideo === op.valor;
                return (
                  <label key={op.valor} htmlFor={`asm-video-${op.valor}`} style={{ ...S.opcion, ...(elegida ? S.opcionElegida : {}) }}>
                    <input
                      id={`asm-video-${op.valor}`}
                      type="radio"
                      name="asm-proveedor-video"
                      value={op.valor}
                      checked={elegida}
                      onChange={() => setForm((f) => ({ ...f, proveedorVideo: op.valor }))}
                      style={{ marginTop: spacing[1] }}
                    />
                    <span style={{ display: "flex", flexDirection: "column", gap: spacing[1] }}>
                      <span style={S.opcionTitulo}>{op.titulo}</span>
                      <span style={S.opcionTexto}>{op.texto}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}
        {conVideo && (
          <p style={{ margin: 0, ...label }}>
            {usaZoom
              ? "La sala se crea sola después de convocar: no hace falta esperarla. El acceso es personal y llega por WhatsApp o email (y en la app) 15 minutos antes."
              : "El link solo se comparte con los propietarios, en su enlace personal."}
          </p>
        )}

        <OrdenDelDiaEditor puntos={form.ordenDelDia} onChange={(ordenDelDia) => setForm((f) => ({ ...f, ordenDelDia }))} />

        <div style={{ background: colors.gray50, borderRadius: radius.sm, padding: spacing[4] }}>
          <div style={{ ...label, marginBottom: spacing[2] }}>Así le llega por WhatsApp a cada propietario (con botones Asistiré / No podré asistir / Ver detalle):</div>
          <p style={{ margin: 0, fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.5 }}>{vistaPrevia}</p>
        </div>

        {error && (
          <p role="alert" style={{ margin: 0, color: colors.errorDark, background: colors.errorLight, padding: spacing[3], borderRadius: radius.sm, fontSize: typography.sizes.base }}>
            {error}
          </p>
        )}
      </form>
    </AdminModal>
  );
}

const S = {
  fieldset: { margin: 0, padding: 0, border: 0, minWidth: 0 },
  legend: { padding: 0, marginBottom: spacing[2], fontSize: typography.sizes.base, color: colors.gray900, fontWeight: 400 },
  opciones: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: spacing[3] },
  opcion: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[3],
    padding: spacing[3],
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    background: colors.white,
    cursor: "pointer",
    transition: `border-color ${transitions.fast}, background ${transitions.fast}`,
  },
  opcionElegida: { border: `1px solid ${colors.primary}`, background: colors.primarySubtle },
  opcionTitulo: { fontSize: typography.sizes.md, color: colors.black, fontWeight: 400 },
  opcionTexto: { fontSize: typography.sizes.sm, color: colors.gray600, fontWeight: 400, lineHeight: 1.4 },
};
